import React from 'react';
import { Button } from '../components/ui/button';
import { Calculator, TrendingUp, PiggyBank, Target, CheckCircle2, ArrowRight } from 'lucide-react';

const WealthManagement = () => {
  const benefits = [
    'Personalized budgeting strategies tailored to your income and goals',
    'Strategic spending plans that maximize your financial resources',
    'Custom savings systems designed for short and long-term objectives',
    'Wealth building strategies for generational financial security',
    'Regular financial check-ins and progress tracking',
    'Tools and resources for ongoing financial management',
    'Legal support for financial planning and documentation',
    'Income shifting strategies to optimize tax efficiency and wealth distribution'
  ];

  const process = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'We meet to understand your current financial situation, goals, and challenges.'
    },
    {
      step: '2',
      title: 'Financial Assessment',
      description: 'Comprehensive analysis of your income, expenses, debts, and savings.'
    },
    {
      step: '3',
      title: 'Custom Strategy Development',
      description: 'We create a personalized wealth management plan tailored to your needs.'
    },
    {
      step: '4',
      title: 'Implementation & Support',
      description: 'Ongoing guidance and adjustments to ensure you stay on track.'
    }
  ];

  const features = [
    {
      icon: Calculator,
      title: 'Budget Planning',
      description: 'Create a realistic budget that works for your lifestyle and income level.'
    },
    {
      icon: TrendingUp,
      title: 'Spending Strategy',
      description: 'Develop smart spending habits that align with your financial goals.'
    },
    {
      icon: PiggyBank,
      title: 'Savings Systems',
      description: 'Build emergency funds and savings for future investments and goals.'
    },
    {
      icon: Target,
      title: 'Goal Setting',
      description: 'Set achievable financial milestones and track your progress.'
    }
  ];

  const additionalServices = [
    {
      title: 'Legal Support',
      description: 'Access professional legal guidance for financial planning documents, estate planning, trust setup, and wealth protection strategies. We connect you with qualified legal experts to ensure your financial plans are legally sound and properly documented.'
    },
    {
      title: 'Income Shifting Strategies',
      description: 'Optimize your tax efficiency through strategic income distribution across family members, business entities, and investment accounts. Learn proven methods to legally minimize tax liability while maximizing wealth accumulation and intergenerational wealth transfer.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-6 rounded-full shadow-lg">
              <Calculator className="h-16 w-16 text-[#0B8FD8]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold mb-6">
            Wealth Management
          </h1>
          <p className="text-lg md:text-xl text-[#2C3E50] leading-relaxed mb-8">
            Take control of your financial future with personalized budgeting, strategic spending plans, 
            and custom savings systems designed to help you achieve your goals.
          </p>
          <Button 
            className="bg-[#0B8FD8] hover:bg-[#0973B0] text-white px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Schedule Your Free Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            What's Included
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="bg-[#C4E5F4] p-4 rounded-full">
                      <Icon className="h-10 w-10 text-[#0B8FD8]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                    {feature.title}
                  </h3>
                  <p className="text-base text-[#2C3E50]">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Additional Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-serif text-[#0A2540] font-bold mb-4">
                  {service.title}
                </h3>
                <p className="text-base text-[#2C3E50] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Benefits of Our Wealth Management Service
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                  <p className="text-lg text-[#2C3E50]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            Our Process
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="bg-[#0B8FD8] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold font-serif">
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                    {item.title}
                  </h3>
                  <p className="text-base text-[#2C3E50]">
                    {item.description}
                  </p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-[#C4E5F4]"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B8FD8] py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-bold mb-6">
            Ready to Take Control of Your Finances?
          </h2>
          <p className="text-lg text-white mb-8">
            Schedule your free consultation today and start building wealth that lasts.
          </p>
          <Button 
            className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default WealthManagement;
